import moment from "moment";
import * as React from "react";
import { createNotificationAPI } from "src/infra/api/service/notification_api";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import { ConstBreakPoint } from "../../config/styles_const/styles_const";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { INotificationRepository } from "../../domain/repository/notification_repository";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import Assets from "../assets/img/assets";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import { TBreadcrumbItem } from "../type/common";

const bodyMaxLength = 200;

const apiClient: IApiClient = createApiClient();
const notificationRepository: INotificationRepository =
  createNotificationAPI(apiClient);

interface IProps {}

const ClientNotifications: React.FC<IProps> = (props) => {
  const [notifications, setNotifications] = React.useState<
    entity.INotification[] | null
  >(null);
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);
  const [expandState, setExpandState] = React.useState<{
    [key: string]: boolean;
  }>({});

  React.useEffect(() => {
    try {
      notificationRepository
        .getAll(1, 10000)
        .then((items) => {
          const state: {
            [key: string]: boolean;
          } = {};
          items.forEach((item) => {
            state[item.id!] = item.body!.length <= bodyMaxLength;
          });

          setExpandState(state);
          setNotifications(items);
        })
        .then(() => {
          notificationRepository.read();
        });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  // 初期化
  if (notifications == null) {
    return <Progress canAnimation={false} />;
  }

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    { text: `お知らせ` },
  ];

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />
        <NotificationList>
          {notifications.map((item, i) => {
            const shouldExpand: boolean = expandState[item.id!];
            const body =
              item.body!.length > bodyMaxLength && !shouldExpand
                ? `${item.body!.substring(0, bodyMaxLength)}...`
                : item.body;
            const shouldShowExpandButton =
              item.body!.length > bodyMaxLength && !shouldExpand;

            return (
              <NotificationItem key={i}>
                <Label read={item.isRead!}>
                  {item.isRead! ? "既読" : "未読"}
                </Label>
                <Date>
                  {moment(item.createdAt!, "YYYY-MM-DD HH:II:SS").format(
                    "YYYY年MM月DD日"
                  )}
                </Date>
                <TitleContainer>
                  <TitleIcon src={Assets.bell} />
                  <Title>{item.title}</Title>
                </TitleContainer>
                <Body>{body}</Body>
                {item.linkURL && (
                  <LinkContainer>
                    <LinkButton href={item.linkURL} target="_blank">
                      {item.linkTitle || "リンク"}
                    </LinkButton>
                  </LinkContainer>
                )}
                {shouldShowExpandButton && (
                  <ExpandButton
                    onClick={() => {
                      const newState: {
                        [key: string]: boolean;
                      } = {};
                      for (const key of Object.keys(expandState)) {
                        newState[key] = expandState[key];
                      }
                      newState[item.id!] = true;
                      setExpandState(newState);
                    }}
                  >
                    全て表示
                  </ExpandButton>
                )}
              </NotificationItem>
            );
          })}
        </NotificationList>
      </PageWrapper>
    </React.Fragment>
  );
};

export default ClientNotifications;

const NotificationList = styled.div`
  width: 100%;
`;

const NotificationItem = styled.div`
  width: 100%;
  border-radius: 10px;
  border: 1px solid #ebf0f1;
  padding: 16px 10px;
  position: relative;
  margin-top: 33px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    padding: 30px 24px;
  }
`;

const Date = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #a4a4a4;
`;

const TitleContainer = styled.div`
  display: flex;
  margin-top: 14px;
  padding-right: 70px;
`;

const TitleIcon = styled.img`
  width: 20px;
  height: 25px;
  color: #9a8c64;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    width: 30px;
    height: 36px;
  }
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: #333333;
  line-height: 26px;
  margin-left: 12px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    font-size: 24px;
    line-height: 36px;
  }
`;

const Body = styled.div`
  font-weight: normal;
  font-size: 12px;
  color: #333333;
  line-height: 24px;
  margin-top: 15px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    font-size: 15px;
  }
  white-space: pre-wrap;
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
`;

const LinkButton = styled.a`
  display: block;
  padding: 7px 26px;
  background-color: #9a8c64;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const Label = styled.div<{ read: boolean }>`
  width: 70px;
  height: 26px;
  text-align: center;
  line-height: 26px;
  background-color: ${({ read }) => (read ? "#D8D8D8" : "#00D58E")};
  color: white;
  font-size: 12px;
  font-weight: bold;
  position: absolute;
  right: 0px;
  top: 30px;
`;

const ExpandButton = styled.div`
  width: 130px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  color: $333333;
  background-color: #eeeeee;
  cursor: pointer;
  margin: 0 auto;
  margin-top: 24px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 30px;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/client_notifications.tsx